import { useId, useState } from 'react';
import {
  ChevronDown,
  KeyRound,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Smartphone,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MfaFactor } from '@/features/Auth/gcp/gcpAuth';
import { SecurityMethodStatus } from '@/features/UserProfile/components/SecurityMethodStatus';
import { Button } from '@/components/ui/button';

export type SecurityMethod = 'totp' | 'phone' | 'email';

/** Presentation only. Enrollment, confirmation and last-factor guards stay in
 * MfaEnrollment, shared with the mandatory authentication flow. */
export function MfaMethodsInventory({
  factors,
  email,
  emailEnabled,
  serverMethods,
  uncertain,
  unavailable,
  canRemove,
  onChoose,
  onRemove,
  onReplace,
}: {
  factors: Array<MfaFactor>;
  email?: string | null;
  emailEnabled: boolean;
  serverMethods: ReadonlyArray<string>;
  uncertain: boolean;
  unavailable: boolean;
  canRemove: boolean;
  onChoose: (method: SecurityMethod) => void;
  onRemove: (factor: MfaFactor | 'email') => void;
  onReplace: (factor: MfaFactor) => void;
}) {
  const { t } = useTranslation('settings');
  const advancedId = useId();
  const [advanced, setAdvanced] = useState(false);
  const methods = [
    {
      id: 'totp',
      name: t('security_design.authenticator', 'Authenticator App'),
      description: t(
        'security_design.authenticator_description',
        'Verification codes from your authenticator app, available offline.',
      ),
      icon: KeyRound,
    },
    {
      id: 'phone',
      name: t('security_design.text_message', 'Text Message'),
      description: t(
        'security_design.text_description',
        'Verification codes sent to your mobile number.',
      ),
      icon: Smartphone,
    },
    {
      id: 'email',
      name: t('security_design.email', 'Email'),
      description: t(
        'security_design.email_description',
        'Verification codes sent to your account email address.',
      ),
      icon: Mail,
    },
  ] as const;
  return (
    <>
      <div className="security-method-list">
        {methods.map(({ id, name, description, icon: Icon }) => {
          const enrolled = factors.filter((factor) => factor.factorId === id);
          const enabled =
            id === 'email'
              ? emailEnabled
              : enrolled.length > 0 || serverMethods.includes(id);
          const records =
            id === 'email' && enabled
              ? [
                  {
                    key: 'email',
                    label: email || name,
                    factor: 'email' as const,
                  },
                ]
              : enrolled.map((factor) => ({
                  key: factor.uid,
                  label: factor.displayName || factor.phoneNumber || name,
                  factor,
                }));
          return (
            <section className="security-method-row" key={id} aria-label={name}>
              <div className="security-method-main">
                <span className="security-method-icon" aria-hidden="true">
                  <Icon />
                </span>
                <div className="security-method-copy">
                  <div className="security-method-name">
                    <h3>{name}</h3>
                    {id === 'totp' && (
                      <span className="security-method-recommended">
                        {t('security_design.recommended', 'Recommended')}
                      </span>
                    )}
                  </div>
                  <p>{description}</p>
                </div>
                <SecurityMethodStatus
                  active={enabled && !uncertain}
                  label={
                    uncertain
                      ? t('security_design.unconfirmed', 'Status Unavailable')
                      : enabled
                        ? t('security_design.enabled', 'Enabled')
                        : t('security_design.not_enabled', 'Not Enabled')
                  }
                />
                <div className="security-method-actions">
                  {(!enabled || (id !== 'email' && enrolled.length === 1)) && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={unavailable}
                      onClick={() =>
                        enabled ? onReplace(enrolled[0]) : onChoose(id)
                      }
                      aria-label={
                        enabled
                          ? id === 'totp'
                            ? t(
                                'security_design.replace_authenticator',
                                'Replace Authenticator App',
                              )
                            : t(
                                'security_design.change_phone',
                                'Change Phone Number',
                              )
                          : `${t('security_design.enable', 'Enable')} ${name.toLowerCase()}`
                      }
                    >
                      {enabled ? (
                        id === 'totp' ? (
                          <RefreshCw aria-hidden="true" />
                        ) : (
                          <Pencil aria-hidden="true" />
                        )
                      ) : (
                        <Plus aria-hidden="true" />
                      )}
                      {enabled
                        ? id === 'totp'
                          ? t('security_design.replace', 'Replace')
                          : t('security_design.change_number', 'Change Number')
                        : t('security_design.enable', 'Enable')}
                    </Button>
                  )}
                </div>
              </div>
              {enabled && (
                <div className="security-method-records">
                  {records.map((record) => (
                    <div className="security-method-record" key={record.key}>
                      <span>{record.label}</span>
                      {enrolled.length > 1 && record.factor !== 'email' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="security-record-replace"
                          disabled={unavailable}
                          onClick={() => onReplace(record.factor)}
                          aria-label={`${id === 'totp' ? t('security_design.replace', 'Replace') : t('security_design.change_number', 'Change Number')} ${record.label}`}
                        >
                          {id === 'totp' ? (
                            <RefreshCw aria-hidden="true" />
                          ) : (
                            <Pencil aria-hidden="true" />
                          )}
                          {id === 'totp'
                            ? t('security_design.replace', 'Replace')
                            : t(
                                'security_design.change_number',
                                'Change Number',
                              )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={unavailable || !canRemove}
                        onClick={() => onRemove(record.factor)}
                        aria-label={
                          record.factor === 'email'
                            ? `${t('security_design.disable', 'Disable')} ${name.toLowerCase()}`
                            : `${t('security_design.disable', 'Disable')} ${record.factor.displayName || (id === 'totp' ? 'Authenticator app' : 'Text message')}`
                        }
                      >
                        {t('security_design.disable', 'Disable')}
                      </Button>
                    </div>
                  ))}
                  {id !== 'email' && enrolled.length === 0 && (
                    <p className="security-method-hint">
                      {t(
                        'security_design.sign_in_again',
                        'Sign out and back in to manage this method on this device.',
                      )}
                    </p>
                  )}
                  {!canRemove && (
                    <p className="security-method-hint">
                      {t(
                        'security_design.last_method',
                        'Enable another method before disabling this one.',
                      )}
                    </p>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
      <p className="security-method-guidance">
        {t(
          'security_design.backup_guidance',
          'Keep at least one method enabled. Add a backup so you can still sign in if your usual method is unavailable.',
        )}
      </p>
      <div className="security-advanced">
        <button
          type="button"
          className="security-advanced-toggle"
          aria-expanded={advanced}
          aria-controls={advancedId}
          onClick={() => setAdvanced(!advanced)}
        >
          <ChevronDown aria-hidden="true" />
          {t(
            'security_design.additional_options',
            'Additional Security Options',
          )}
        </button>
        <div
          className="security-method-reveal"
          data-open={advanced}
          id={advancedId}
          inert={!advanced}
          aria-hidden={!advanced}
        >
          <div>
            <div className="security-backup-row">
              <span className="security-method-icon">
                <KeyRound aria-hidden="true" />
              </span>
              <div>
                <h3>
                  {t(
                    'security_design.backup_authenticator',
                    'Backup Authenticator',
                  )}
                </h3>
                <p>
                  {t(
                    'security_design.backup_description',
                    'An additional authenticator gives you another way to sign in if your usual device is unavailable.',
                  )}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={unavailable}
                onClick={() => onChoose('totp')}
              >
                <Plus aria-hidden="true" />
                {t('security_design.add_backup', 'Add Backup App')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
